export interface ThreadPosition {
  top: number;
  shown: boolean;
}

// Constants
export const THREAD_POSITION_OFFSET = 30;
export const DEFAULT_THREAD_POSITION_TOP = 80;
export const THREAD_CARD_HEIGHT = 60;
export const THREAD_SPACING = 12;
export const THREAD_STACK_OVERLAP = 12;
export const THREAD_GROUP_CENTER_DIVISOR = 5; // Used to offset group by 20% of total height for centering
export const DEFAULT_THREAD_POSITION: ThreadPosition = { top: DEFAULT_THREAD_POSITION_TOP, shown: false };

export class ThreadPositionManager {
  private threadIdToPosition = new Map<string, number>();
  private groupAnchorToDelta = new Map<number, number>();

  clear(): void {
    this.threadIdToPosition.clear();
    this.groupAnchorToDelta.clear();
  }

  getThreadPositions(): readonly number[] {
    return Array.from(this.threadIdToPosition.values()).sort((a, b) => a - b);
  }

  hasThread(threadId: string): boolean {
    return this.threadIdToPosition.has(threadId);
  }

  getThreadPosition(threadId: string): number | undefined {
    return this.threadIdToPosition.get(threadId);
  }

  private adjustPositionForOverlap(proposedTop: number, threadId: string): number {
    const minSpacing = THREAD_CARD_HEIGHT + THREAD_SPACING;

    if (this.threadIdToPosition.has(threadId)) {
      const existingPosition = this.threadIdToPosition.get(threadId);
      return existingPosition || proposedTop;
    }

    let adjustedTop = proposedTop;

    for (const existingTop of this.threadIdToPosition.values()) {
      const distance = Math.abs(adjustedTop - existingTop);
      if (distance < minSpacing) {
        adjustedTop = Math.max(adjustedTop, existingTop + minSpacing);
      }
    }

    this.threadIdToPosition.set(threadId, adjustedTop);

    return adjustedTop;
  }

  private adjustTopAfterPrevious(proposedTop: number): number {
    if (this.threadIdToPosition.size === 0) return proposedTop;

    let prevBottom = 0;
    for (const top of this.threadIdToPosition.values()) {
      if (top <= proposedTop) {
        prevBottom = Math.max(prevBottom, top + THREAD_CARD_HEIGHT);
      }
    }
    return Math.max(proposedTop, prevBottom + THREAD_SPACING);
  }

  calculateSingleThreadPosition(proposedTop: number, threadId: string): number {
    return this.adjustPositionForOverlap(proposedTop, threadId);
  }

  calculateGroupThreadPosition(
    proposedTop: number,
    threadIndex: number,
    totalThreadsInGroup: number,
    isOpenedGroup: boolean,
    groupKey: number
  ): number {
    let adjustedTop = proposedTop;

    if (isOpenedGroup) {
      const totalHeight = totalThreadsInGroup * (THREAD_CARD_HEIGHT + THREAD_SPACING);
      const centerOffset = totalHeight / THREAD_GROUP_CENTER_DIVISOR;
      const threadOffset = threadIndex * (THREAD_CARD_HEIGHT + THREAD_SPACING);
      adjustedTop = adjustedTop - centerOffset + threadOffset;
    } else {
      adjustedTop += threadIndex * THREAD_STACK_OVERLAP;
    }

    if (threadIndex === 0) {
      const before = adjustedTop;
      const adjusted = isOpenedGroup ? this.adjustTopAfterPrevious(before) : this.adjustPositionForOverlap(before, `${groupKey}-thread-0`);
      const delta = adjusted - before;
      this.groupAnchorToDelta.set(groupKey, delta);
      adjustedTop = adjusted;
      // Ensure the first thread in the group occupies space
      if (isOpenedGroup) {
        this.threadIdToPosition.set(`${groupKey}-thread-0`, adjustedTop);
      }
    } else {
      const delta = this.groupAnchorToDelta.get(groupKey) || 0;
      adjustedTop = adjustedTop + delta;
      this.threadIdToPosition.set(`${groupKey}-thread-${threadIndex}`, adjustedTop);
    }

    return adjustedTop;
  }
}
